import React from "react";
import { Link } from "react-router-dom";
import { NauticusLogo } from "@/components/NauticusLogo";
import { PoweredByFooter } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Compass, Users, LibraryBig, ShieldCheck, ArrowRight, Umbrella, Navigation, CheckCircle2,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1549890762-75fd80457275?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxsaWdodGhvdXNlJTIwc3Vuc2V0fGVufDB8fHx8MTc4ODE0NTAyNXww&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: Users, title: "Build your household", text: "Add your spouse, partner, and children so coverage fits your real family." },
  { icon: LibraryBig, title: "Understand your benefits", text: "Plain-language explanations — no insurance jargon, no walls of text." },
  { icon: Compass, title: "Compare with clarity", text: "See what you're eligible for and weigh your options side by side." },
  { icon: ShieldCheck, title: "Enroll with confidence", text: "Make your elections and get a clear confirmation you can revisit anytime." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-mist-100">
      {/* Nav */}
      <header className="absolute top-0 inset-x-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <NauticusLogo light />
          <div className="flex items-center gap-3">
            <Link to="/ask" className="hidden sm:inline-flex">
              <Button className="bg-gold text-navy-900 hover:bg-gold-light rounded-full px-5 font-bold" data-testid="nav-ask-nauticus">
                Ask Nauticus
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10" data-testid="nav-login">
                Sign in
              </Button>
            </Link>
            <Link to="/register" className="hidden sm:inline-flex">
              <Button className="bg-gold text-navy-900 hover:bg-gold-light rounded-full px-5 font-bold" data-testid="nav-register">
                Get started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center">
        <img src={HERO} alt="Lighthouse guiding at dusk" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 nauticus-hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl animate-rise">
            <span className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.25em] uppercase font-bold mb-6">
              <Navigation className="w-4 h-4" /> Benefits, guided
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]">
              Benefits that finally make sense for your whole household.
            </h1>
            <p className="mt-6 text-lg text-mist-200 leading-relaxed max-w-xl">
              Nauticus guides you and your family through understanding, comparing, and enrolling in
              your benefits — clearly, calmly, and without the jargon.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4">
              <Link to="/ask">
                <Button size="lg" className="bg-gold text-navy-900 hover:bg-gold-light rounded-full px-8 font-bold h-12 w-full sm:w-auto" data-testid="hero-ask-nauticus">
                  Ask Nauticus <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/login" className="text-mist-200 hover:text-white text-sm font-medium underline underline-offset-4 self-center" data-testid="hero-signin">
                Already have an account? Sign in
              </Link>
            </div>
            <p className="mt-4 text-sm text-mist-200">
              New to Nauticus?{" "}
              <Link to="/register" className="text-gold font-semibold hover:text-gold-light underline underline-offset-4" data-testid="hero-create-account">
                Create your account
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="max-w-2xl mb-14">
          <span className="text-gold-dark text-xs tracking-[0.22em] uppercase font-bold">How Nauticus works</span>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-navy-900 tracking-tight mt-3">
            A calm path from confused to covered.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white rounded-xl border border-mist-200 p-8 card-lift" data-testid={`step-card-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-navy-800 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-gold" />
                </div>
                <div className="text-gold-dark font-bold text-sm mb-1">Step {i + 1}</div>
                <h3 className="font-display text-xl font-bold text-navy-900 mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Household band */}
      <section className="bg-navy-800">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div>
            <span className="inline-flex items-center gap-2 text-gold text-xs tracking-[0.22em] uppercase font-bold mb-5">
              <Umbrella className="w-4 h-4" /> Household-first
            </span>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight">
              Designed around your family — not an insurance company.
            </h2>
            <p className="mt-5 text-mist-200 leading-relaxed">
              Most benefits tools are built for advisors and carriers. Nauticus is built for you. Set up
              your household once and every recommendation, eligibility check, and election is tailored to
              the people you're protecting.
            </p>
            <ul className="mt-8 space-y-3">
              {["Employee, spouse & dependents in one place", "Eligibility evaluated per household member", "Elections and confirmations you can revisit anytime"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-mist-100">
                  <CheckCircle2 className="w-5 h-5 text-gold shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <NauticusLogo light />
          <div className="flex flex-col sm:items-end gap-1">
            <p className="text-mist-300 text-sm">
              Nauticus is a standalone benefits technology & enrollment platform.
            </p>
            <PoweredByFooter light />
          </div>
        </div>
      </footer>
    </div>
  );
}
